import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { MyRegistrations } from '../components/MyRegistrations';
import { UpcomingEvents } from '../components/UpcomingEvents';
import { EditProfileModal } from '../components/EditProfileModal';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { NotificationSettingsModal } from '../components/NotificationSettingsModal';
import { AdSense } from '../components/AdSense';

export function UserDashboard() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [userData, setUserData] = useState(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [hasScannerPermission, setHasScannerPermission] = useState(false);
  const [scannerPermissionChecked, setScannerPermissionChecked] = useState(false);

  useEffect(() => {
    const email = localStorage.getItem('user_email');
    const userId = localStorage.getItem('user_id');
    if (email) {
      setUserEmail(email);
    }
    if (userId) {
      fetchUserData(userId);
      fetchScannerPermission(userId);
    }
  }, []);

  const fetchUserData = async (userId) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`/api/users/${userId}`, {
        headers: {
          'X-Session-Token': sessionToken,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchScannerPermission = async (userId) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`/api/users/${userId}/has-permission/ticket.scanner`, {
        headers: {
          'X-Session-Token': sessionToken,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setHasScannerPermission(data.success && data.hasPermission === true);
      }
    } catch (error) {
      console.error('Error checking scanner permission:', error);
    } finally {
      setScannerPermissionChecked(true);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken) {
          await fetch('/api/logout', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Session-Token': sessionToken,
            },
          });
        }
      } catch (error) {
        console.error('Logout error:', error);
      }

      // Clear session
      localStorage.removeItem('session_token');
      localStorage.removeItem('user_role');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_id');
      
      navigate('/login', { replace: true });
    }
  };

  return (
    <>
      <Navbar userRole="user" onLogout={handleLogout} />

      <div className="container py-5">
        <h1 className="mb-4">User Dashboard</h1>
        
        <div className="alert alert-info mb-4">
          <strong>Logged in as: {userData?.first_name} </strong> {userEmail}
        </div>

        <div className="row">
          <div className="col-md-8">
            <div className="card mb-4">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Dashboard Overview</h5>
              </div>
              <div className="card-body">
                <p>Welcome to your dashboard!</p>
              </div>
            </div>

            {hasScannerPermission && (
              <div className="card mb-4">
                <div className="card-header bg-info text-white">
                  <h5 className="mb-0">📷 Ticket Scanner Access</h5>
                </div>
                <div className="card-body">
                  <p className="mb-3">You have permission to verify tickets with the scanner.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/admin/scanner')}
                  >
                    Open Ticket Scanner
                  </button>
                </div>
              </div>
            )}

            <MyRegistrations />

            <div className="card mb-4 mt-4">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0">📺 Available Streams</h5>
              </div>
              <div className="card-body">
                <p className="text-muted">Streams content coming soon...</p>
              </div>
            </div>

            <UpcomingEvents />
          </div>

          <div className="col-md-4">
            <div className="card">
              <div className="card-header bg-warning text-dark">
                <h5 className="mb-0">⚙️ Account Settings</h5>
              </div>
              <div className="card-body">
                <ul className="list-group list-group-flush">
                  <li className="list-group-item">
                    <a 
                      href="#" 
                      className="text-decoration-none"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowEditProfile(true);
                      }}
                    >
                      Edit Profile
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a 
                      href="#" 
                      className="text-decoration-none"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowChangePassword(true);
                      }}
                    >
                      Change Password
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a 
                      href="#" 
                      className="text-decoration-none"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowNotificationSettings(true);
                      }}
                    >
                      Notification Settings
                    </a>
                  </li>
                  <li className="list-group-item">
                    <button 
                      onClick={handleLogout} 
                      className="btn btn-outline-danger w-100 mt-2"
                    >
                      🔓 Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditProfileModal 
        isOpen={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        user={userData}
        onSuccess={() => {
          const userId = localStorage.getItem('user_id');
          if (userId) fetchUserData(userId);
        }}
      />
      <ChangePasswordModal 
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
      <NotificationSettingsModal 
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
      />
      <AdSense />
    </>
  );
}

export default UserDashboard;
